import { useTranslation } from "next-i18next";

import type { ProductQuality } from "@/Apollo/schema";
import { useAuth } from "@/auth/hooks";
import { getBrowserToken } from "@/auth/store";
import { QUALITY_MAP } from "@/common/static/product-attributes.static";
import { type OpenHomeCopy, useOpenHomeCopy } from "@/features/open-home/copy";
import { useOpenHomeGate } from "@/features/open-home/gate";
import {
  type Box,
  type ListingDetails,
  type LocalizedTitle,
  type Offer,
  type PhotoRefusalReason,
  type PriceRange,
  type PublicItem,
  type PublicRoom,
  type Room,
  isPhotoRefusalReason,
} from "@/features/open-home/model";
import { trackOpenHomeAction } from "@/features/open-home/telemetry";
import { changedKeys } from "@/features/open-home/telemetry-events";
import {
  type QueryKey,
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";

/**
 * Browser side of Open Home: one fetch helper and the react-query hooks the pages use. The API is
 * our own Next route (not the gateway), so this is react-query + fetch like the search feature,
 * not an Apollo hook. Every call carries the member's bearer token.
 */

export class ApiError extends Error {
  constructor(
    public status: number,
    message: string,
    /** Why the safety scan refused a photo (422), when it did. */
    public reason: PhotoRefusalReason | null = null
  ) {
    super(message);
  }
}

/** What the owner reads for a failed call: a refused photo in their language, else the API's text. */
export const errorText = (copy: OpenHomeCopy, error: Error) =>
  error instanceof ApiError && error.reason
    ? `${copy.photoRefused[error.reason]} ${copy.photoRefusedMistake}`
    : error.message;

/**
 * The bearer for an Open Home call. Since WHO-6344 it is a 5-minute token, and the copy in the
 * auth store lapses when a background tab's timers sleep or a click beats the refresh. Ask for
 * the current one, as the Apollo auth link does; the store's copy is only the fallback.
 */
const currentToken = async (token: string | null) =>
  (await getBrowserToken()) ?? token;

/** Retry what the server could not answer (5xx, network); a 4xx is an answer. */
export const retryServerErrors = (count: number, error: Error) =>
  !(error instanceof ApiError && error.status < 500) && count < 2;

export const openHomeFetch = async <T>(
  storedToken: string | null,
  path: string,
  init?: { method?: string; body?: unknown }
): Promise<T> => {
  const token = await currentToken(storedToken);
  const [resource, query] = path.split("?");
  const method = init?.method ?? "GET";
  const response = await fetch(
    `/api/open-home?p=${resource}${query ? `&${query}` : ""}`,
    {
      method,
      headers: {
        "content-type": "application/json",
        // The page language (<html lang>): titles are written and read in it.
        "x-open-home-lang":
          typeof document === "undefined" ? "" : document.documentElement.lang,
        ...(token ? { authorization: `Bearer ${token}` } : {}),
      },
      body: init?.body === undefined ? undefined : JSON.stringify(init.body),
    }
  ).catch((error: unknown) => {
    // Offline or blocked: a failed attempt too (status 0).
    trackOpenHomeAction(method, path, 0);
    throw error;
  });
  const payload = await response.json().catch(() => ({}));
  const refusal = isPhotoRefusalReason(payload?.reason) ? payload.reason : null;

  trackOpenHomeAction(
    method,
    path,
    response.status,
    {
      oh_changed: method === "PATCH" ? changedKeys(init?.body) : undefined,
      oh_offer_action:
        init?.body && typeof init.body === "object" && "action" in init.body
          ? String((init.body as { action: unknown }).action)
          : undefined,
    },
    refusal
  );

  if (!response.ok) {
    throw new ApiError(
      response.status,
      payload?.error ?? response.statusText,
      refusal
    );
  }

  return payload as T;
};

export { useOpenHomeCopy };

/** Condition labels from the same keys a normal PDP uses, so the words match everywhere. */
export const useConditionLabel = () => {
  const { t } = useTranslation();

  return (condition: string | null | undefined) =>
    condition && condition in QUALITY_MAP
      ? t(QUALITY_MAP[condition as ProductQuality])
      : "";
};

export const imageUrl = (roomId: string, photoId: string) =>
  `/api/open-home?p=image/${roomId}/${photoId}`;

/** Images go through our API with the bearer token (it proxies the gateway), read as object URLs. */
export const useRoomImage = (
  roomId: string | undefined,
  photoId: string | undefined
) => {
  const { token } = useAuth();

  return useQuery({
    queryKey: ["open-home", "image", roomId, photoId, Boolean(token)],
    enabled: Boolean(token && roomId && photoId),
    staleTime: Infinity,
    queryFn: async () => {
      const bearer = await currentToken(token);
      const response = await fetch(
        imageUrl(roomId as string, photoId as string),
        {
          headers: bearer ? { authorization: `Bearer ${bearer}` } : undefined,
          // Revalidate: after the owner blurs more, the browser must not show its old copy.
          cache: "no-cache",
        }
      );

      if (!response.ok) {
        throw new ApiError(response.status, "image");
      }

      return URL.createObjectURL(await response.blob());
    },
  });
};

const useOpenHomeQuery = <T>(key: QueryKey, path: string | null) => {
  const { token, isReady } = useAuth();

  return useQuery({
    queryKey: ["open-home", ...key, Boolean(token)],
    enabled: isReady && Boolean(token) && path !== null,
    queryFn: () => openHomeFetch<T>(token, path as string),
    retry: retryServerErrors,
    // Editors hold local state (open rows, a half-typed room name): a refetch must never swap
    // the tree for a loading state. No refetch on window focus, and the last data stays on screen
    // while a refetch or a key change loads.
    refetchOnWindowFocus: false,
    placeholderData: keepPreviousData,
  });
};

export type Me = {
  id: string;
  firstName: string;
  isAdmin: boolean;
  commission: number;
  commissionIncludesVat: boolean;
  rooms: number;
};

export const useMe = () => useOpenHomeQuery<Me>(["me"], "me");

export const useStatus = () =>
  useQuery({
    queryKey: ["open-home", "status"],
    queryFn: () =>
      openHomeFetch<{ store: boolean; recognition: boolean }>(null, "status"),
    staleTime: 60_000,
  });

export const useMyRooms = () =>
  useOpenHomeQuery<{ rooms: Room[] }>(["rooms"], "rooms");

export type SearchResult = {
  rooms: PublicRoom[];
  items: { item: PublicItem; room: { id: string; name: string } }[];
  hasOwnRoom?: boolean;
};

export const useOpenHomeSearch = (
  filters: Record<string, string | undefined>
) => {
  const params = new URLSearchParams(
    Object.entries(filters).filter((entry): entry is [string, string] =>
      Boolean(entry[1])
    )
  ).toString();

  return useOpenHomeQuery<SearchResult>(["search", params], `search?${params}`);
};

/** The buyer's view of a room (also for its owner, so they see what buyers see). */
export const usePublicRoom = (roomId: string | undefined) =>
  useOpenHomeQuery<{ room: PublicRoom; owner: boolean }>(
    ["room", roomId, "buyer"],
    roomId ? `rooms/${roomId}?as=buyer` : null
  );

export type OfferView = Omit<Offer, "sellerId" | "buyerId"> & {
  role: "seller" | "buyer";
  item: {
    id: string;
    title: LocalizedTitle;
    photoId: string;
    photoWidth: number;
    photoHeight: number;
    box: Box;
    status: string;
    listingDetails: ListingDetails | null;
    condition: string | null;
    estimatedSize: string | null;
    categoryPath: string | null;
  } | null;
  room: {
    id: string;
    name: string;
  } | null;
};

/**
 * The sold-price range of the piece open in the review flow, fetched for that piece only.
 * `available` is false when no range can be asked for; the recognition's median hint stays.
 */
export const useItemPriceRange = (itemId: string, condition: string | null) =>
  useOpenHomeQuery<{ available: boolean; range: PriceRange | null }>(
    ["price-range", itemId, condition ?? "unknown"],
    `price-range/${itemId}`
  );

export const useOffers = () =>
  useOpenHomeQuery<{ offers: OfferView[] }>(["offers"], "offers");

export type FavoriteEntry = {
  item: PublicItem;
  room: { id: string; name: string };
  photo: { id: string; width: number; height: number };
  savedAt: string;
};

export const useFavorites = () =>
  useOpenHomeQuery<{ favorites: FavoriteEntry[] }>(["favorites"], "favorites");

export type FavoriteLink = {
  productId: string;
  roomId: string;
  itemId: string;
};

/**
 * Open Home pieces among the member's regular favorites (gateway backend only), keyed by product
 * id. Fail-soft: any error means no labels, never a broken Favorites page.
 */
export const useOpenHomeFavoriteLinks = () => {
  const { token } = useAuth();
  const query = useQuery({
    queryKey: ["open-home", "favorite-links", Boolean(token)],
    enabled: Boolean(token),
    retry: false,
    queryFn: () =>
      openHomeFetch<{ links: FavoriteLink[] }>(token, "favorite-links"),
  });

  return new Map(
    (query.data?.links ?? []).map(link => [link.productId, link] as const)
  );
};

/** "roomId/itemId" keys of the member's saved pieces, for the hearts. */
export const useFavoriteIds = () => {
  const favorites = useFavorites();

  return new Set(
    (favorites.data?.favorites ?? []).map(
      favorite => `${favorite.room.id}/${favorite.item.id}`
    )
  );
};

export const useToggleFavorite = () =>
  useOpenHomeMutation((token, input: { roomId: string; itemId: string }) =>
    openHomeFetch<{ saved: boolean }>(token, "favorites", {
      method: "POST",
      body: input,
    })
  );

/**
 * Rooms the member saved (the Rooms tab in Favorites). `available` is false until the gateway
 * knows saved rooms; then there is no "Save room" and no tab. Fail-soft like the favorite links.
 */
export const useSavedRooms = () => {
  const { token } = useAuth();
  const on = useOpenHomeGate();

  return useQuery({
    queryKey: ["open-home", "saved-rooms", Boolean(token)],
    enabled: Boolean(token) && on,
    retry: false,
    queryFn: () =>
      openHomeFetch<{ available: boolean; rooms: PublicRoom[] }>(
        token,
        "saved-rooms"
      ),
  });
};

export const useToggleSavedRoom = () =>
  useOpenHomeMutation((token, input: { roomId: string; saved: boolean }) =>
    openHomeFetch<{ saved: boolean }>(token, "saved-rooms", {
      method: "POST",
      body: input,
    })
  );

/** Mutations invalidate all of Open Home: the demo is small and every view is cheap. */
export const useOpenHomeMutation = <TInput, TResult = unknown>(
  run: (token: string | null, input: TInput) => Promise<TResult>
) => {
  const { token } = useAuth();
  const client = useQueryClient();

  return useMutation({
    mutationFn: (input: TInput) => run(token, input),
    onSuccess: () =>
      client.invalidateQueries({
        predicate: query =>
          query.queryKey[0] === "open-home" && query.queryKey[1] !== "image",
      }),
  });
};

/** Downscale in the browser before upload: 1600 px JPEG keeps a room photo around 0.5 MB. */
export const downscale = (file: File, maxEdge = 1600): Promise<string> =>
  new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const image = new Image();

    image.onload = () => {
      const scale = Math.min(1, maxEdge / Math.max(image.width, image.height));
      const canvas = document.createElement("canvas");

      canvas.width = Math.round(image.width * scale);
      canvas.height = Math.round(image.height * scale);
      canvas
        .getContext("2d")
        ?.drawImage(image, 0, 0, canvas.width, canvas.height);
      URL.revokeObjectURL(url);
      resolve(canvas.toDataURL("image/jpeg", 0.86));
    };
    image.onerror = reject;
    image.src = url;
  });

export const formatEur = (amount: number, locale?: string) =>
  new Intl.NumberFormat(locale === "nl" ? "nl-NL" : "en-GB", {
    style: "currency",
    currency: "EUR",
    maximumFractionDigits: 0,
  }).format(amount);
